// frontend/src/utils/secuenciaAdapter.ts
import type {
  SecuenciaProduccionRow, ComponenteSecuenciaRow,
} from '@/types';
import type {
  ProductionOrder, OrderComponent, OrderStatus, AcabadoType, ComponentStatus,
} from '@/types/productionSequence';
import { computeAdherencia } from './sequenceCalculations';

/** status crudo de prodtable (0-based de este lakehouse) → OrderStatus del frontend. */
export function mapStatus(status: number): OrderStatus {
  switch (status) {
    case 1: return 'ESTIMADO';
    case 2: return 'PROGRAMADO';
    case 3: return 'LIBERADO';
    case 4: return 'INICIADO';            // Iniciado → en curso, reordenable
    case 5: return 'NOTIFICADO_TERMINADO';
    case 7: return 'TERMINADO';
    default: return 'ESTIMADO';
  }
}

function asAcabado(v: string | null | undefined): AcabadoType {
  return v === 'SEMI' || v === 'RG' || v === 'TST' ? v : 'SEMI';
}

function asComponentStatus(v: string | null | undefined): ComponentStatus {
  return v === 'OK' || v === 'PENDIENTE_CALIDAD' || v === 'ENTRE_ALMACENES' || v === 'COMPRAS_EN_CURSO' || v === 'ROTURA_STOCK'
    ? v : 'OK';
}

export function mapRowToOrder(row: SecuenciaProduccionRow): ProductionOrder {
  const udOT = row.unidades ?? 0;
  const udProducidas = row.unidadesProducidas ?? 0;
  return {
    id: row.produccion,
    empresa: row.empresa,
    lineId: row.rutaPrl ?? '(sin línea)',
    ruta: row.routeid ?? '',
    referencia: row.articulo ?? '',
    familia: row.familia ?? '',
    subfamilia: row.subfamilia ?? '',
    acabado: asAcabado(row.acabado),
    descripcion: row.nombreArticulo ?? '',
    esReferenciaNueva: row.referenciaNueva ?? false,
    udOT,
    udProducidas,
    udPendientes: row.unidadesPendientes ?? Math.max(0, udOT - udProducidas),
    estado: mapStatus(row.status),
    fechaPedido: row.fechaPedido ?? null,
    fechaPlanificadaFin: row.fechaFin ?? null,
    fechaInicioPlan: row.fechaInicio ?? null,
    fechaRealFin: row.fechaRealFin ?? null,
    adherenciaPct: row.adherenciaPct || computeAdherencia(udProducidas, udOT),
    comentarios: row.comentarios ?? '',
    motivoDesviacion: row.motivoDesviacion ?? '',
    campoCalidad: row.campoCalidad ?? null,
    granelStatus: row.estadoGranel === 'OK' ? 'OK' : 'PENDIENTE',
    granelFecha: row.fechaGranel ?? null,
    componentesStatus: asComponentStatus(row.estadoComponentes),
    componentesFecha: row.fechaComponentes ?? null,
    componentes: [],                       // se rellena en attachComponentes
    componentesRecalc: [],                 // se rellena en recalcComponentStatus
    upm: row.upm ?? null,
    udTurnoPromedio: row.udTurnoPromedio ?? null,
    nOperarios: row.cantidadOperarios ?? null,
    sequencePosition: 0,
    isManuallyReordered: false,
    isRecalculated: false,
  };
}

export function attachComponentes(
  orders: ProductionOrder[],
  comps: ComponenteSecuenciaRow[],
): ProductionOrder[] {
  const byOT = new Map<string, OrderComponent[]>();
  for (const c of comps) {
    const list = byOT.get(c.produccion) ?? [];
    list.push({
      itemId: c.itemId,
      inventcolorid: c.inventcolorid ?? null,
      inventstyleid: c.inventstyleid ?? null,
      inventlocationid: c.inventlocationid ?? null,
      consumoEstimado: c.consumoEstimado,
      reserva: c.reserva,
      pendiente: c.pendiente,
      stockCalidadSpain: c.stockCalidadSpain ?? null,
      esGranel: (c.itemId ?? '').toUpperCase().startsWith('Q'),
    });
    byOT.set(c.produccion, list);
  }
  return orders.map(o => ({ ...o, componentes: byOT.get(o.id) ?? [] }));
}
